import React, { useEffect, useState, useRef } from 'react';
import { X, Camera, Image as ImageIcon, Trash, House, Buildings, LinkedinLogo, FacebookLogo, Info } from '@phosphor-icons/react';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Spinner } from '../ui/Spinner';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { cn } from '../../lib/utils';
const ACCEPTED_IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
export interface LandlordEditData {
  avatar: string;
  firstName: string;
  lastName: string;
  subRole: 'proprietar' | 'agent' | null;
  agencyName: string;
  phone: string;
  website: string;
  linkedinUrl: string;
  facebookUrl: string;
  bio: string;
}
export type LandlordEditFocusSection = 'social' | 'bio' | 'identity';
interface EditLandlordProfileModalProps {
  open: boolean;
  initialData: LandlordEditData;
  onClose: () => void;
  onSave: (data: LandlordEditData) => void;
  focusSection?: LandlordEditFocusSection;
}
type ErrorMap = Partial<Record<keyof LandlordEditData, string>>;
export function EditLandlordProfileModal({
  open,
  initialData,
  onClose,
  onSave,
  focusSection
}: EditLandlordProfileModalProps) {
  const [data, setData] = useState<LandlordEditData>(initialData);
  const [errors, setErrors] = useState<ErrorMap>({});
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const [photoError, setPhotoError] = useState<string | undefined>();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const socialRef = useRef<HTMLDivElement>(null);
  const bioRef = useRef<HTMLDivElement>(null);
  const identityRef = useRef<HTMLDivElement>(null);
  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setData(initialData);
      setErrors({});
      setSaving(false);
      setPhotoSheetOpen(false);
      setPhotoError(undefined);
    }
  }, [open, initialData]);
  // Scroll to focus section
  useEffect(() => {
    if (!open || !focusSection) return;
    const refMap = {
      social: socialRef,
      bio: bioRef,
      identity: identityRef
    };
    const ref = refMap[focusSection];
    if (ref?.current) {
      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [open, focusSection]);
  // Close photo sheet on outside click
  useEffect(() => {
    if (!photoSheetOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-photo-sheet]')) return;
      setPhotoSheetOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [photoSheetOpen]);
  const hasChanges = JSON.stringify(data) !== JSON.stringify(initialData);
  const handleClose = () => {
    if (hasChanges) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  };
  const handlePhotoFile = (file: File) => {
    setPhotoError(undefined);
    if (!ACCEPTED_IMG_TYPES.includes(file.type)) {
      setPhotoError('Format invalid. Folosește JPG, PNG sau WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Fișierul este prea mare. Maxim 5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target?.result) {
        setData(prev => ({
          ...prev,
          avatar: e.target!.result as string
        }));
        setPhotoSheetOpen(false);
      }
    };
    reader.readAsDataURL(file);
  };
  const handleRemovePhoto = () => {
    setData(prev => ({
      ...prev,
      avatar: ''
    }));
    setPhotoSheetOpen(false);
  };
  const handleSubRoleChoice = (choice: 'proprietar' | 'agent') => {
    setData(prev => ({
      ...prev,
      subRole: choice,
      agencyName: choice === 'agent' ? prev.agencyName : ''
    }));
  };
  const validate = (): boolean => {
    const errs: ErrorMap = {};
    if (!data.firstName.trim()) errs.firstName = 'Prenumele este obligatoriu';
    if (!data.lastName.trim()) errs.lastName = 'Numele este obligatoriu';
    if (!data.subRole) errs.subRole = 'Alege tipul de cont';
    if (data.website && !data.website.match(/^https?:\/\/.+/)) {
      errs.website = 'URL invalid (trebuie să înceapă cu http:// sau https://)';
    }
    if (data.linkedinUrl && !data.linkedinUrl.match(/^https?:\/\/.+/)) {
      errs.linkedinUrl = 'URL invalid';
    }
    if (data.facebookUrl && !data.facebookUrl.match(/^https?:\/\/.+/)) {
      errs.facebookUrl = 'URL invalid';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    await new Promise(r => setTimeout(r, 600));
    onSave(data);
    setSaving(false);
  };
  if (!open) return null;
  const initials = `${data.firstName[0] || ''}${data.lastName[0] || ''}`.toUpperCase() || 'P';
  const subRoleLocked = !!initialData.subRole;
  const isAgent = data.subRole === 'agent';
  return <>
      <div role="dialog" aria-modal="true" aria-label="Editează profilul" className="fixed inset-0 z-50 bg-dark-1/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 lg:pl-[16rem]" onClick={handleClose} data-id="element-1092">
        <div className="w-full sm:max-w-2xl bg-dark-2 sm:rounded-2xl rounded-t-2xl border-t sm:border border-dark-3 flex flex-col max-h-[100vh] sm:max-h-[90vh]" onClick={e => e.stopPropagation()} data-id="element-1093">
          {/* Sticky header — desktop only */}
          <div className="hidden lg:flex sticky top-0 z-10 bg-dark-2 border-b border-dark-3 px-6 py-4 items-center justify-between rounded-t-2xl" data-id="element-1094">
            <h2 className="text-xl font-semibold text-light-1" data-id="element-1095">
              Editează profilul
            </h2>
            <div className="flex items-center gap-3" data-id="element-1096">
              <button type="button" onClick={handleClose} className="p-2 rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Închide" data-id="element-1097">
                <X size={20} weight="bold" data-id="element-1098" />
              </button>
              <button type="button" onClick={handleSave} disabled={saving || !hasChanges} className={cn('h-10 px-4 rounded-md text-sm font-semibold transition-colors', saving || !hasChanges ? 'bg-dark-3 text-light-4 cursor-not-allowed' : 'bg-lime-1 text-dark-1 hover:bg-lime-2 cursor-pointer')} data-id="element-1099">
                {saving ? <Spinner size="sm" data-id="element-1100" /> : 'Salvează'}
              </button>
            </div>
          </div>

          {/* Mobile/tablet header — simple X */}
          <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-dark-3 shrink-0" data-id="element-1101">
            <h2 className="text-base font-semibold text-light-1" data-id="element-1102">
              Editează profilul
            </h2>
            <button type="button" onClick={handleClose} className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Închide" data-id="element-1103">
              <X size={18} weight="bold" data-id="element-1104" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-6 space-y-8" data-id="element-1105">
            {/* PHOTO SECTION */}
            <section data-id="element-1106">
              <h3 className="text-base font-semibold text-light-1 mb-4" data-id="element-1107">
                Fotografie de profil
              </h3>
              <div className="flex items-center gap-4" data-id="element-1108">
                <div className="shrink-0" data-id="element-1109">
                  {data.avatar ? <img src={data.avatar} alt="Avatar" className="w-20 h-20 rounded-full object-cover" data-id="element-1110" /> : <div className="w-20 h-20 rounded-full bg-dark-3 flex items-center justify-center text-xl font-medium text-light-2" data-id="element-1111">
                      {initials}
                    </div>}
                </div>
                <div className="relative flex-1" data-id="element-1112">
                  <button type="button" onClick={() => setPhotoSheetOpen(v => !v)} className="px-4 py-2 rounded-md text-sm font-medium bg-dark-3 text-light-1 hover:bg-dark-4 transition-colors cursor-pointer" data-id="element-1113">
                    {data.avatar ? 'Schimbă fotografia' : 'Adaugă fotografie'}
                  </button>
                  {photoSheetOpen && <div data-photo-sheet className="absolute left-0 top-full mt-2 w-56 bg-dark-2 border border-dark-3 rounded-md shadow-2xl overflow-hidden z-20" data-id="element-1114">
                      <button type="button" onClick={() => cameraInputRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-1115">
                        <Camera size={18} data-id="element-1116" />
                        Fă o poză
                      </button>
                      <button type="button" onClick={() => galleryInputRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-1117">
                        <ImageIcon size={18} data-id="element-1118" />
                        Încarcă din galerie
                      </button>
                      {data.avatar && <button type="button" onClick={handleRemovePhoto} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-1 hover:bg-red-4 transition-colors cursor-pointer" data-id="element-1119">
                          <Trash size={18} data-id="element-1120" />
                          Elimină fotografia
                        </button>}
                    </div>}
                  <input ref={cameraInputRef} type="file" accept={ACCEPTED_IMG_TYPES.join(',')} capture="user" className="hidden" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handlePhotoFile(file);
                  e.target.value = '';
                }} data-id="element-1121" />
                  <input ref={galleryInputRef} type="file" accept={ACCEPTED_IMG_TYPES.join(',')} className="hidden" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handlePhotoFile(file);
                  e.target.value = '';
                }} data-id="element-1122" />
                </div>
              </div>
              {photoError && <p className="text-xs text-red-1 mt-2" data-id="element-1123">{photoError}</p>}
            </section>

            {/* IDENTITY SECTION */}
            <section ref={identityRef} data-id="element-1124">
              <h3 className="text-base font-semibold text-light-1 mb-4" data-id="element-1125">
                Identitate
              </h3>
              <div className="space-y-4" data-id="element-1126">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1127">
                  <Input label="Prenume" value={data.firstName} onChange={e => setData(prev => ({
                  ...prev,
                  firstName: e.target.value
                }))} error={errors.firstName} data-id="element-1128" />
                  <Input label="Nume" value={data.lastName} onChange={e => setData(prev => ({
                  ...prev,
                  lastName: e.target.value
                }))} error={errors.lastName} data-id="element-1129" />
                </div>

                {/* Sub-role */}
                <div data-id="element-1130">
                  <label className="block text-sm font-medium text-light-2 mb-2" data-id="element-1131">
                    Tip de cont
                  </label>
                  {subRoleLocked ? <>
                      <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-dark-3 border border-dark-4" data-id="element-1132">
                        {data.subRole === 'proprietar' ? <House size={18} className="text-light-3" data-id="element-1133" /> : <Buildings size={18} className="text-light-3" data-id="element-1134" />}
                        <span className="text-sm text-light-1 flex-1" data-id="element-1135">
                          {data.subRole === 'proprietar' ? 'Proprietar' : 'Agent imobiliar'}
                        </span>
                        <Info size={16} className="text-light-4" data-id="element-1136" />
                      </div>
                      <p className="text-xs text-light-4 mt-1.5" data-id="element-1137">
                        Pentru a modifica tipul contului, contactează{' '}
                        <a href="mailto:support@izio.ro" className="text-lime-1 hover:text-lime-2" data-id="element-1138">
                          support@izio.ro
                        </a>
                      </p>
                    </> : <>
                      <div className="grid grid-cols-2 gap-3" data-id="element-1139">
                        <button type="button" onClick={() => handleSubRoleChoice('proprietar')} className={cn('flex items-center gap-2 px-4 py-3 rounded-md border transition-colors cursor-pointer', data.subRole === 'proprietar' ? 'bg-lime-5 border-lime-4 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-dark-3')} data-id="element-1140">
                          <House size={18} data-id="element-1141" />
                          <span className="text-sm font-medium" data-id="element-1142">
                            Proprietar
                          </span>
                        </button>
                        <button type="button" onClick={() => handleSubRoleChoice('agent')} className={cn('flex items-center gap-2 px-4 py-3 rounded-md border transition-colors cursor-pointer', data.subRole === 'agent' ? 'bg-lime-5 border-lime-4 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-dark-3')} data-id="element-1143">
                          <Buildings size={18} data-id="element-1144" />
                          <span className="text-sm font-medium" data-id="element-1145">
                            Agent imobiliar
                          </span>
                        </button>
                      </div>
                      {errors.subRole && <p className="text-xs text-red-1 mt-1.5" data-id="element-1146">
                          {errors.subRole}
                        </p>}
                    </>}
                </div>

                {/* Agency name — only when agent */}
                {isAgent && <Input label="Numele agenției (opțional)" placeholder="ex: Imobiliare Premium" value={data.agencyName} onChange={e => setData(prev => ({
                ...prev,
                agencyName: e.target.value
              }))} data-id="element-1147" />}

                {/* Email — display only */}
                <div data-id="element-1148">
                  <label className="block text-sm font-medium text-light-2 mb-2" data-id="element-1149">
                    Email
                  </label>
                  <div className="px-4 py-3 rounded-md bg-dark-3 border border-dark-4 text-sm text-light-3" data-id="element-1150">
                    {data.firstName.toLowerCase()}@example.com
                  </div>
                  <p className="text-xs text-light-4 mt-1.5" data-id="element-1151">
                    Emailul nu poate fi modificat
                  </p>
                </div>
              </div>
            </section>

            {/* SOCIAL SECTION */}
            <section ref={socialRef} data-id="element-1152">
              <h3 className="text-base font-semibold text-light-1 mb-4" data-id="element-1153">
                Social media
              </h3>
              <div className="space-y-4" data-id="element-1154">
                <div data-id="element-1155">
                  <label className="block text-sm font-medium text-light-2 mb-2" data-id="element-1156">
                    <span className="inline-flex items-center gap-2" data-id="element-1157">
                      <LinkedinLogo size={16} weight="fill" data-id="element-1158" />
                      LinkedIn (opțional)
                    </span>
                  </label>
                  <Input placeholder="https://linkedin.com/in/..." value={data.linkedinUrl} onChange={e => setData(prev => ({
                  ...prev,
                  linkedinUrl: e.target.value
                }))} error={errors.linkedinUrl} data-id="element-1159" />
                </div>
                <div data-id="element-1160">
                  <label className="block text-sm font-medium text-light-2 mb-2" data-id="element-1161">
                    <span className="inline-flex items-center gap-2" data-id="element-1162">
                      <FacebookLogo size={16} weight="fill" data-id="element-1163" />
                      Facebook (opțional)
                    </span>
                  </label>
                  <Input placeholder="https://facebook.com/..." value={data.facebookUrl} onChange={e => setData(prev => ({
                  ...prev,
                  facebookUrl: e.target.value
                }))} error={errors.facebookUrl} data-id="element-1164" />
                </div>
              </div>
            </section>

            {/* CONTACT SECTION */}
            <section data-id="element-1165">
              <h3 className="text-base font-semibold text-light-1 mb-4" data-id="element-1166">
                Contact
              </h3>
              <div className="space-y-4" data-id="element-1167">
                <Input label="Telefon (opțional)" type="tel" placeholder="ex: 0712 345 678" value={data.phone} onChange={e => setData(prev => ({
                ...prev,
                phone: e.target.value
              }))} data-id="element-1168" />
                <Input label={isAgent ? 'Website agenție (opțional)' : 'Website (opțional)'} type="url" placeholder="ex: https://example.com" value={data.website} onChange={e => setData(prev => ({
                ...prev,
                website: e.target.value
              }))} error={errors.website} data-id="element-1169" />
              </div>
            </section>

            {/* BIO SECTION */}
            <section ref={bioRef} data-id="element-1170">
              <h3 className="text-base font-semibold text-light-1 mb-4" data-id="element-1171">
                {isAgent ? 'Despre mine / agenție' : 'Despre mine'}
              </h3>
              <Textarea placeholder={isAgent ? 'Câteva cuvinte despre tine / agenția ta — experiență, specializare, zone preferate...' : 'Câteva cuvinte despre tine ca proprietar — abordarea ta, ce aștepți de la chiriași...'} value={data.bio} onChange={e => setData(prev => ({
              ...prev,
              bio: e.target.value.slice(0, 500)
            }))} rows={5} data-id="element-1172" />
              <p className="text-xs text-light-4 mt-1.5 text-right" data-id="element-1173">
                {data.bio.length} / 500
              </p>
            </section>
          </div>

          {/* Sticky footer — mobile/tablet only */}
          <div className="lg:hidden sticky bottom-0 z-10 bg-dark-2 border-t border-dark-3 px-5 py-3 shrink-0" data-id="element-1174">
            <button type="button" onClick={handleSave} disabled={saving || !hasChanges} className={cn('w-full h-12 rounded-md text-sm font-semibold transition-colors', saving || !hasChanges ? 'bg-dark-3 text-light-4 cursor-not-allowed' : 'bg-lime-1 text-dark-1 hover:bg-lime-2 cursor-pointer')} data-id="element-1175">
              {saving ? <Spinner size="sm" data-id="element-1176" /> : 'Salvează'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog open={confirmDiscard} title="Renunți la modificări?" description="Modificările nesalvate vor fi pierdute." confirmLabel="Renunță" cancelLabel="Continuă editarea" variant="warning" dashboardOffset onConfirm={() => {
      setConfirmDiscard(false);
      onClose();
    }} onCancel={() => setConfirmDiscard(false)} data-id="element-1177" />
    </>;
}